import React, { useState, useEffect } from 'react';
import Select, { createFilter } from 'react-select';
import { companiesAPI, partiesAPI, billsAPI } from '../services/api';
import { billToOption } from '../utils/billOption';

const ApiSelect = ({
  type, // 'companies', 'parties', 'bills'
  value = null,
  onChange,
  placeholder = 'Select an option',
  isClearable = true,
  isDisabled = false,
  className = '',
  companyId = null, // For bills filtering
  partyId = null,   // For bills filtering
  fetchAll = false, // For bills: fetch all bills regardless of company/party
  fy = null,        // For bills: restrict options to one financial year ('' / null = all years)
  extraOptions = [], // Options kept in the list even when outside the fetched set (see below)
  isMulti = false, // Enable multi-select mode
  ...props
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      let response;

      switch (type) {
        case 'companies':
          response = await companiesAPI.getAll({ limit: 1000 }); // Get all companies for dropdown
          const companies = response.data.companies ? response.data.companies : response.data;
          const companyOptions = companies.map(company => ({
            value: company._id,
            label: company.name,
            data: company
          }));
          setOptions(companyOptions);
          break;

        case 'parties':
          response = await partiesAPI.getAll({ limit: 1000 }); // Get all parties for dropdown
          const parties = response.data.parties ? response.data.parties : response.data;
          const partyOptions = parties.map(party => ({
            value: party._id,
            label: party.name,
            data: party
          }));
          setOptions(partyOptions);
          break;

        case 'bills':
          // Restrict to one financial year so the dropdown doesn't list bills from prior
          // years. Omitted when fy is empty ("All Years" in the picker).
          const fyParam = fy ? { fy } : {};
          if (fetchAll) {
            // Fetch all bills when fetchAll is true
            response = await billsAPI.getAll({ all: 'true', ...fyParam });
          } else if (!companyId || !partyId) {
            setOptions([]);
            break;
          } else {
            response = await billsAPI.getAll({ company: companyId, party: partyId, ...fyParam });
          }
          // Sort by bill number ascending (numeric by first part, e.g. "123/2024" -> 123)
          const billNumberSortKey = (billNumber) => {
            const num = parseInt(String(billNumber).split('/')[0]);
            return isNaN(num) ? Infinity : num;
          };
          const bills = response.data.bills
            .slice()
            .sort((a, b) => {
              const aNum = billNumberSortKey(a.billNumber);
              const bNum = billNumberSortKey(b.billNumber);
              if (aNum !== bNum) return aNum - bNum;
              return String(a.billNumber).localeCompare(String(b.billNumber));
            })
            // billNumber is not unique across companies, so options key on company + billNumber
            .map(billToOption);
          setOptions(bills);
          break;

        default:
          setOptions([]);
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [type, companyId, partyId, fetchAll, fy]);

  const handleChange = (selectedOption) => {
    if (onChange) {
      onChange(selectedOption);
    }
  };

  // An already-selected item can fall outside the fetched set — e.g. editing a payment whose
  // bill belongs to a different financial year than the one being filtered on. Without this it
  // would vanish from the dropdown and read as "no bill attached".
  const seenValues = new Set(options.map(option => option.value));
  const mergedOptions = [
    ...extraOptions.filter(option => option.value && !seenValues.has(option.value)),
    ...options
  ];

  // Find the selected option(s) based on value
  const getSelectedOption = () => {
    if (isMulti) {
      // For multi-select, value should be an array
      if (Array.isArray(value)) {
        if (value.length === 0) return [];
        const selected = value.map(val => mergedOptions.find(option => option.value === val)).filter(Boolean);
        return selected.length > 0 ? selected : [];
      }
      return [];
    } else {
      // For single select
      if (!value) return null;
      // Value can be a string or object
      if (typeof value === 'string') {
        return mergedOptions.find(option => option.value === value) || null;
      }
      return value;
    }
  };

  const selectedOption = getSelectedOption();

  return (
    <Select
      options={mergedOptions}
      value={selectedOption}
      onChange={handleChange}
      placeholder={loading ? 'Loading...' : placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled || loading}
      isLoading={loading}
      isMulti={isMulti}
      // Filter on the visible label only. The option `value` is an ObjectId whose hex contains
      // stray digits, so the default filter (label + value) matches almost everything when
      // typing a number.
      filterOption={createFilter({ stringify: (option) => option.label })}
      className={className}
      classNamePrefix="react-select"
      styles={{
        control: (base) => ({
          ...base,
          minHeight: '42px',
          borderColor: '#E0E7FF',
          borderRadius: '12px',
          '&:hover': {
            borderColor: '#A5B4FC'
          }
        }),
        placeholder: (base) => ({
          ...base,
          color: '#94A3B8',
          fontSize: '14px'
        }),
        menu: (base) => ({
          ...base,
          borderRadius: '12px',
          border: '1px solid #E0E7FF',
          boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.1)'
        })
      }}
      {...props}
    />
  );
};

export default ApiSelect;
